import { useId } from 'react';
import { setClarityConsent, useClarityConsent } from '../../lib/clarityConsent';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';

/** Asks once for analytics cookies. Without a project id there is nothing to ask about. */
export function ClarityConsentBanner({ projectId }: { projectId: string | undefined }) {
  const consent = useClarityConsent();
  if (!projectId || consent !== null) return null;

  return (
    <section
      aria-label="Cookie consent"
      className="fixed inset-x-4 bottom-4 z-50 rounded-xl border border-border bg-card p-5 shadow-lg sm:left-auto sm:w-full sm:max-w-md"
    >
      <h2 className="m-0 text-heading-h7 text-foreground">We use cookies</h2>
      <p className="mt-2 mb-0 text-body-sm-regular text-muted-foreground">
        With your permission, we'd like to use analytics cookies, including session recordings
        with page content masked, to improve Discovr. You can change this at any time in Settings.
        Read our{' '}
        <a href="/privacy-policy" target="_blank" rel="noreferrer" className="text-foreground underline">
          Privacy Policy
        </a>
        .
      </p>
      <div className="mt-4 flex gap-2">
        <Button className="flex-1" onClick={() => setClarityConsent(false)}>
          Reject
        </Button>
        <Button className="flex-1" onClick={() => setClarityConsent(true)}>
          Accept
        </Button>
      </div>
    </section>
  );
}

/** The Settings control for changing the answer later. */
export function ClarityConsentSetting({ projectId }: { projectId: string | undefined }) {
  const consent = useClarityConsent();
  const switchId = useId();
  if (!projectId) return null;

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <h3 className="m-0 text-heading-h7 text-foreground">Cookies</h3>
      <div className="mt-3 flex items-center justify-between gap-4">
        <div>
          <Label htmlFor={switchId} className="text-body-sm-medium text-foreground">
            Analytics cookies
          </Label>
          <p className="m-0 text-caption-regular text-muted-foreground">
            Session recordings with page content masked, to help us improve Discovr.
          </p>
        </div>
        <Switch id={switchId} checked={consent === true} onCheckedChange={setClarityConsent} />
      </div>
    </div>
  );
}
